/**
 * Organizations API Slice
 *
 * This module provides RTK Query endpoints for organization management.
 * It uses the auto-generated endpoints from the OpenAPI spec and re-exports
 * them with user-friendly names.
 */

import { enhancedApi } from '@store/generated/organizations';

/**
 * Organizations API - enhanced from generated endpoints with cache tag invalidation
 */
export const organizationsApi = enhancedApi.enhanceEndpoints({
  addTagTypes: [
    'Subscription',
    'DataSecurityStatus',
    'Members',
    'SubscriptionDetails',
    'UserInfo',
    'Invoices',
    'Entitlements',
    'PermissionsMatrix',
  ],
  endpoints: {
    // Members endpoints - provide/invalidate members list cache
    getV2ProviderOrganizationsMembers: {
      providesTags: ['Members'],
    },
    patchV2ProviderOrganizationsMembersByUserIdProfile: {
      invalidatesTags: ['Members'],
    },
    patchV2ProviderOrganizationsMembersByUserIdBillingInfo: {
      invalidatesTags: ['Members'],
    },

    // Membership info (co-sign settings)
    getV2ProviderOrganizationsMembersByUserIdMembershipInfo: {
      providesTags: (_result, _error, arg) => [
        { type: 'Members' as const, id: `membership-${arg.userId}` },
      ],
    },
    patchV2ProviderOrganizationsMembersByUserIdMembershipInfo: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Members' as const, id: `membership-${arg.userId}` },
      ],
    },

    // Member bundle management - invalidate subscription details and entitlements when bundles are added
    postV2ProviderOrganizationsMembersByUserIdBundles: {
      invalidatesTags: ['Subscription', 'SubscriptionDetails', 'UserInfo', 'Invoices', 'Entitlements'],
    },
    // Member bundle change - invalidate subscription details and entitlements when bundle is changed
    putV2ProviderOrganizationsMembersByUserIdBundlesAndAssignmentId: {
      invalidatesTags: ['Subscription', 'SubscriptionDetails', 'UserInfo', 'Invoices', 'Entitlements'],
    },
    // Member bundle removal - invalidate subscription details and entitlements when bundle is removed
    deleteV2ProviderOrganizationsMembersByUserIdBundlesAndAssignmentId: {
      invalidatesTags: ['Subscription', 'SubscriptionDetails', 'UserInfo', 'Invoices', 'Entitlements'],
    },

    // Org-level bundle management - invalidate subscription details and data security status when org bundles are added
    postV2ProviderOrganizationsBundles: {
      invalidatesTags: [
        'Subscription',
        'DataSecurityStatus',
        'SubscriptionDetails',
        'UserInfo',
        'Invoices',
      ],
    },
    putV2ProviderOrganizationsBundlesByAssignmentId: {
      invalidatesTags: ['Subscription', 'Invoices'],
    },
    deleteV2ProviderOrganizationsBundlesByAssignmentId: {
      invalidatesTags: ['Subscription', 'Invoices'],
    },

    // Data Security endpoints - query (provide tags)
    getV2ProviderOrganizationsDataSecurity: { providesTags: ['DataSecurityStatus'] },

    // Data Security endpoints - mutations (invalidate tags to refetch status)
    postV2ProviderOrganizationsBaa: { invalidatesTags: ['DataSecurityStatus'] },
    postV2ProviderOrganizationsCoi: { invalidatesTags: ['DataSecurityStatus'] },
    postV2ProviderOrganizationsDataRetention: { invalidatesTags: ['DataSecurityStatus'] },

    // Permissions matrix (ADR 0073) - query provides the matrix cache; the
    // mutation invalidates the matrix and UserInfo so derived permissions refetch.
    getV2ProviderOrganizationsPermissions: { providesTags: ['PermissionsMatrix'] },
    patchV2ProviderOrganizationsPermissions: {
      invalidatesTags: ['PermissionsMatrix', 'UserInfo'],
    },
  },
});

// Export hooks with user-friendly names for provider endpoints
export const {
  // Organization info
  useGetV2ProviderOrganizationsQuery: useGetOrganizationQuery,

  // Members management
  useGetV2ProviderOrganizationsMembersQuery: useGetOrganizationMembersQuery,
  useGetV2ProviderOrganizationsMembersByUserIdDetailQuery: useGetMemberDetailQuery,
  useDeleteV2ProviderOrganizationsMembersByUserIdMutation: useRemoveMemberMutation,
  usePostV2ProviderV2ProviderOrganizationsMembersByUserIdReactivateMutation:
    useReactivateMemberMutation,
  usePatchV2ProviderOrganizationsMembersByUserIdRolesMutation: useUpdateMemberRolesMutation,
  usePatchV2ProviderOrganizationsMembersByUserIdProfileMutation: useUpdateMemberProfileMutation,
  usePatchV2ProviderOrganizationsMembersByUserIdBillingInfoMutation: useUpdateMemberBillingInfoMutation,
  useGetV2ProviderOrganizationsMembersByUserIdMembershipInfoQuery: useGetMembershipInfoQuery,
  usePatchV2ProviderOrganizationsMembersByUserIdMembershipInfoMutation: useUpdateMembershipInfoMutation,

  // Member bundle management
  usePostV2ProviderOrganizationsMembersByUserIdBundlesMutation: useAddMemberBundleMutation,
  usePutV2ProviderOrganizationsMembersByUserIdBundlesAndAssignmentIdMutation:
    useChangeMemberBundleMutation,
  useDeleteV2ProviderOrganizationsMembersByUserIdBundlesAndAssignmentIdMutation:
    useRemoveMemberBundleMutation,

  // Org-level bundle management
  usePostV2ProviderOrganizationsBundlesMutation: useAddOrgBundleMutation,
  usePutV2ProviderOrganizationsBundlesByAssignmentIdMutation: useReplaceOrgBundleMutation,
  useDeleteV2ProviderOrganizationsBundlesByAssignmentIdMutation: useRemoveOrgBundleMutation,

  // Invitations management
  useGetV2ProviderOrganizationsInvitationsQuery: useGetInvitationsQuery,
  usePostV2ProviderOrganizationsInvitationsMutation: useCreateInvitationMutation,
  usePatchV2ProviderOrganizationsInvitationsByInvitationIdMutation: useUpdateInvitationMutation,
  useDeleteV2ProviderOrganizationsInvitationsByInvitationIdMutation: useDeleteInvitationMutation,
  usePostV2ProviderOrganizationsInvitationsByInvitationIdResendMutation:
    useResendInvitationMutation,

  // Invitation acceptance (used by accept-invitation feature)
  useGetV2ProviderOrganizationsInvitationsValidateByTokenQuery: useValidateInvitationQuery,
  usePostV2ProviderOrganizationsInvitationsAcceptMutation: useAcceptInvitationMutation,

  // Roles
  useGetV2ProviderOrganizationsRolesQuery: useGetAvailableRolesQuery,

  // Templates management
  useGetV2ProviderOrganizationsTemplatesQuery: useGetTemplatesQuery,
  usePostV2ProviderOrganizationsTemplatesMutation: useAddTemplateMutation,
  usePutV2ProviderOrganizationsTemplatesByTemplateIdMutation: useUpdateTemplateMutation,
  useDeleteV2ProviderOrganizationsTemplatesByTemplateIdMutation: useRemoveTemplateMutation,

  // Forms management
  useGetV2ProviderOrganizationsFormsQuery: useGetFormsQuery,
  usePostV2ProviderOrganizationsFormsMutation: useAddFormMutation,
  useDeleteV2ProviderOrganizationsFormsByFormIdMutation: useRemoveFormMutation,

  // Patient visibility settings
  useGetV2ProviderOrganizationsPatientVisibilityQuery: useGetPatientVisibilityQuery,
  usePatchV2ProviderOrganizationsPatientVisibilityMutation: useUpdatePatientVisibilityMutation,

  // Data Security hooks
  useGetV2ProviderOrganizationsDataSecurityQuery: useGetDataSecurityStatusQuery,
  usePostV2ProviderOrganizationsBaaMutation: useSubmitBAAMutation,
  usePostV2ProviderOrganizationsCoiMutation: useSubmitCOIMutation,
  usePostV2ProviderOrganizationsDataRetentionMutation: useSubmitDataRetentionMutation,

  // Permissions matrix (ADR 0073)
  useGetV2ProviderOrganizationsPermissionsQuery: useGetPermissionsMatrixQuery,
  usePatchV2ProviderOrganizationsPermissionsMutation: useUpdatePermissionsMatrixMutation,
} = organizationsApi;

// Re-export types for convenience
export type {
  BaaRequest,
  BaaStatusResponse,
  CoiAddressRequest,
  CoiRequest,
  CoiStatusResponse,
  CombinedDataSecurityStatusResponse,
  DataRetentionRequest,
  DataRetentionStatusResponse,
  MemberBillingInfo,
  MemberDetailResponse,
  MembershipInfoResponse,
  ProviderInvitationAcceptRequest,
  ProviderInvitationAcceptResponse,
  ProviderInvitationValidationResponse,
  ProviderLicenseType,
  UpdateMemberBillingInfoRequest,
  UpdateMemberProfileRequest,
  UpdateMembershipInfoRequest,
  // Permissions matrix (ADR 0073) - now emitted by codegen from the backend contract
  ProviderPermissionRoleGrant,
  ProviderPermissionMatrixItem,
  ProviderPermissionsMatrixResponse,
  ProviderUpdatePermissionsMatrixRequest,
} from '@store/generated/organizations';
